/**
 * Country Selector Component
 *
 * Autocomplete combobox for selecting a country from a curated list.
 * Uses Popover + filtered list (no cmdk dependency needed).
 * Follows the same controlled value/onChange pattern as TimezoneSelector.
 */

import { Check, ChevronsUpDown } from "lucide-react";
import { useId, useMemo, useRef, useState } from "react";
import { buttonVariants } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { COUNTRIES } from "@/constants/countries";
import { cn } from "@/lib/utils";

interface CountrySelectorProps {
	value: string;
	onChange: (country: string) => void;
	label?: string;
	placeholder?: string;
}

export function CountrySelector({
	value,
	onChange,
	label = "Country",
	placeholder = "Select country...",
}: CountrySelectorProps) {
	const id = useId();
	const [open, setOpen] = useState(false);
	const [search, setSearch] = useState("");
	const inputRef = useRef<HTMLInputElement>(null);

	const filtered = useMemo(
		() =>
			search
				? COUNTRIES.filter((c) => c.toLowerCase().includes(search.toLowerCase()))
				: [...COUNTRIES],
		[search]
	);

	const select = (country: string) => {
		onChange(country === value ? "" : country);
		setSearch("");
		setOpen(false);
	};

	return (
		<div className="space-y-2">
			<Label htmlFor={id}>{label}</Label>
			<Popover
				open={open}
				onOpenChange={(next) => {
					setOpen(next);
					if (next) setTimeout(() => inputRef.current?.focus(), 0);
					else setSearch("");
				}}
			>
				<PopoverTrigger asChild>
					<button
						id={id}
						type="button"
						role="combobox"
						aria-expanded={open}
						className={cn(
							buttonVariants({ variant: "outline" }),
							"w-full justify-between font-normal"
						)}
					>
						<span className={cn(!value && "text-muted-foreground")}>{value || placeholder}</span>
						<ChevronsUpDown className="opacity-50" />
					</button>
				</PopoverTrigger>
				<PopoverContent className="w-(--radix-popover-trigger-width) p-0" align="start">
					<div className="p-2">
						<Input
							ref={inputRef}
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							placeholder="Search countries..."
							className="h-8"
						/>
					</div>
					<div className="max-h-60 overflow-y-auto px-1 pb-1">
						{filtered.length === 0 ? (
							<p className="py-4 text-center text-sm text-muted-foreground">No countries found.</p>
						) : (
							filtered.map((country) => (
								<button
									key={country}
									type="button"
									onClick={() => select(country)}
									className="relative flex w-full cursor-default items-center rounded-sm py-1.5 pr-2 pl-8 text-sm outline-none hover:bg-accent hover:text-accent-foreground"
								>
									<Check
										className={cn(
											"absolute left-2 size-4",
											value === country ? "opacity-100" : "opacity-0"
										)}
									/>
									{country}
								</button>
							))
						)}
					</div>
				</PopoverContent>
			</Popover>
		</div>
	);
}
